import React from "react";
import { useFormContext } from "react-hook-form";

import Badge from "@/components/atoms/Badge";
import { Icons } from "@/components/atoms/Icon";
import InputContainer from "@/components/forms/controls/InputContainer";
import { cn } from "@/helpers/className";
import useFormError from "@/hooks/useFormError";

interface ToggleProps
  extends Omit<React.ComponentProps<"input">, "id" | "aria-describedby"> {
  badgeIcon?: Icons;
  badgeIconClassName?: string;
  checked?: boolean;
  checkedClassName?: string;
  id?: string;
  label: string;
  labelClasses?: string;
  name: string;
  onCheckedChange?: (name: string, value: string[]) => void;
  selectedFilters?: { type: string; value: string }[];
  value?: string;
}

/**
 * Toggle component
 *  Acts exactly like a checkbox, but with different styling
 *
 * @param label
 * @param labelClasses
 * @param labelSuffix
 * @param name
 * @param onCheckedChange
 * @param className
 * @param value
 * @param checked
 * @param selectedFilters
 * @param props
 * @constructor
 */
const Toggle = ({
  badgeIcon,
  badgeIconClassName,
  checkedClassName,
  label,
  labelClasses,
  name,
  onCheckedChange,
  className,
  value,
  checked,
  selectedFilters,
  ...props
}: ToggleProps) => {
  const {
    register,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useFormContext();
  const error = useFormError(name, errors);
  const id = props.id ?? `${name}-${String(value)}`;
  const checkedValue = value ?? label;

  const currentValue = watch(name) as string[];
  const isChecked = Object.values(currentValue).includes(checkedValue);

  const handleOnCheck = (checked: boolean) => {
    let oldValue = getValues(name) as string[];

    if (!Array.isArray(oldValue)) {
      oldValue = [];
    }

    let newValue: string[];

    if (checked) {
      oldValue.push(checkedValue);
      // Remove duplicates
      newValue = [...new Set(oldValue)];
    } else {
      newValue = oldValue.filter((value) => value !== checkedValue);
    }

    setValue(name, newValue);
    onCheckedChange?.(name, newValue);
  };

  const checkboxChecked = () => {
    if (selectedFilters === undefined && checked === undefined) {
      return undefined;
    }

    if (checked !== undefined) {
      return checked;
    }

    return selectedFilters?.some(
      (filter) => filter.type === name && filter.value === checkedValue,
    );
  };

  return (
    <InputContainer
      className={cn("relative flex items-start", className)}
      isError={Boolean(error)}
      error={error}
    >
      <input
        type="checkbox"
        id={id}
        value={checkedValue}
        // handleOnCheck
        checked={checkboxChecked()}
        className="hidden"
        {...register(name)}
        {...props}
        onChange={(e) => handleOnCheck(e.target.checked)}
      />

      <label htmlFor={id} className={cn(labelClasses)}>
        <Badge
          color="white"
          icon={badgeIcon}
          iconClassName={badgeIconClassName}
          className={cn(
            "!border-2 border-transparent text-sm transition hover:border-yellow-300",
            isChecked ? checkedClassName : "",
          )}
        >
          {label}
        </Badge>
      </label>
    </InputContainer>
  );
};

export default Toggle;
